// src/pages/CustomerManagement/EditItem.tsx

import React, { useState, useEffect } from "react";
import styles from "./EditItem.module.scss";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";

interface Customer {
  id: number;
  full_name: string;
  promo_code: string;
  email: string;
  ssn: string;
  spouse_id: number | null;
  updatedat: string;
  taxdome_id: string;
  discount: boolean;
  appointment: boolean;
}

interface Invoice {
  id: number;
  invoice: string;
  user_id: number | null;
  full_name: string;
  email: string;
  appointment: boolean;
}

const EditItem: React.FC = () => {
  const { type, id } = useParams<{
    type: "customer" | "invoice";
    id: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [invoiceData, setInvoiceData] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (type === "customer") {
          const res = await api.get(`/get_customer`, {
            params: { id },
          });

          setCustomerData(res.data.customer);
          setFormData(res.data.customer);
        } else if (type === "invoice") {
          const res = await api.get(`/get_invoice`, {
            params: { id },
          });

          setInvoiceData(res.data.id);
          setFormData(res.data.id);
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load data.");
      }
    };

    fetchData();
  }, [type, id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const name = target.name;
    let value: string | boolean;

    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      value = target.checked;
    } else {
      value = target.value;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    setModifiedFields((prev) => new Set(prev).add(name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // 构建仅包含修改字段的对象
    const dataToUpdate: any = {};
    modifiedFields.forEach((field) => {
      dataToUpdate[field] = formData[field];
    });

    // 确保标识符字段存在
    if (type === "customer") {
      dataToUpdate.id = customerData?.id;
    } else if (type === "invoice") {
      dataToUpdate.id = invoiceData?.id;
    }

    if (Object.keys(dataToUpdate).length === 0) {
      setError("No changes to update.");
      return;
    }

    try {
      if (type === "customer") {
        await api.post("/update_customer", dataToUpdate);
        setSuccess("Customer updated successfully.");
      } else if (type === "invoice") {
        await api.post("/update_invoice", dataToUpdate);
        setSuccess("Invoice updated successfully.");
      }

      // 导航回客户管理页面或显示成功消息
      setTimeout(() => {
        navigate("/customer_management");
      }, 2000);
    } catch (err: any) {
      console.error("Update failed:", err);
      setError(err.response?.data?.error || "Update failed.");
    }
  };

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (
    (type === "customer" && !customerData) ||
    (type === "invoice" && !invoiceData)
  ) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.editItemContainer}>
      <h1>{type === "customer" ? "Edit Customer" : "Edit Invoice"}</h1>
      <form onSubmit={handleSubmit} className={styles.editForm}>
        {type === "customer" && customerData && (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="full_name">
                {t("customerManagement.fullName")} *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="ssn">{t("customerManagement.ssn")}</label>
              <input
                type="text"
                id="ssn"
                name="ssn"
                value={formData.ssn}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="spouse_id">
                {t("customerManagement.spouseId")}
              </label>
              <input
                type="number"
                id="spouse_id"
                name="spouse_id"
                value={formData.spouse_id || ""}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">{t("customerManagement.email")} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="taxdome_id">
                {t("customerManagement.taxdomeID")}
              </label>
              <input
                type="text"
                id="taxdome_id"
                name="taxdome_id"
                value={formData.taxdome_id || ""}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGroupCheckbox}>
              <input
                type="checkbox"
                id="discount"
                name="discount"
                checked={formData.discount}
                onChange={handleChange}
              />
              <label htmlFor="discount">
                {t("customerManagement.applyDiscount")}
              </label>
            </div>

            <div className={styles.formGroupCheckbox}>
              <input
                type="checkbox"
                id="appointment"
                name="appointment"
                checked={formData.appointment}
                onChange={handleChange}
              />
              <label htmlFor="appointment">
                {t("customerManagement.appointment")}
              </label>
            </div>
          </>
        )}

        {type === "invoice" && invoiceData && (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="invoice">
                {t("customerManagement.invoice")} *
              </label>
              <input
                type="text"
                id="invoice"
                name="invoice"
                value={formData.invoice}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="user_id">{t("customerManagement.userId")}</label>
              <input
                type="integer"
                id="user_id"
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGroupCheckbox}>
              <input
                type="checkbox"
                id="appointment"
                name="appointment"
                checked={formData.appointment}
                onChange={handleChange}
              />
              <label htmlFor="appointment">
                {t("customerManagement.appointment")}
              </label>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="full_name">
                {t("customerManagement.fullName")} *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">{t("customerManagement.email")} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </>
        )}

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        <div className={styles.formActions}>
          <button type="submit" className={styles.submitButton}>
            {t("general.save")}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => navigate("/customer_management")}
          >
            {t("general.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditItem;
